import FiscalServiceElectron from '@/wrappers/FiscalServiceElectron'
import FiscalServiceAndroid from '@/wrappers/FiscalServiceAndroid'
import FiscalServiceWeb from '@/wrappers/FiscalServiceWeb'
import Device from '@/wrappers/Device';
import Store from '@/store'
import i18n from '@/i18n';

const ESDC_URL_KEY = 'esdc-url';

export default {
    service: null,
    device: null,

    esdcUrl: null,
    useDevEsdc: process.env.VUE_APP_ENV === 'staging',

    currentTaxRates: null,
    allTaxRates: [],

    queue: [],
    curJob: false,
    queueSetUp: false,

    async init(options = {}) {
        const noncashOnly = !!Store.getters.tenant?.venue?.settings?.noncash_only;
        const device = Device.get();
        if(locale !== 'sr' || noncashOnly || device?.platform === 'web') return;

        this.device = device;

        let savedEsdcUrl = this.getEsdcUrl();
        if(!savedEsdcUrl) {
            savedEsdcUrl = process.env.VUE_APP_ESDC_URL
            AppStorage.local().setItem(ESDC_URL_KEY, savedEsdcUrl, null);
        }

        this.esdcUrl = options.esdcUrl || savedEsdcUrl;
        options.esdcUrl = this.esdcUrl;

        const certificate = await AppStorage.native().getItem('cert')
        const certificateAlias = await AppStorage.native().getItem('certAlias')
        this.isCertificateSetup = !!certificate || !!certificateAlias

        if (this.device.platform === 'electron') {
            this.service = new FiscalServiceElectron();
        } else if (this.device.platform === 'android') {
            this.service = new FiscalServiceAndroid();
        } else {
            this.service = new FiscalServiceWeb();
        }

        await this.service.init(options)

        this.setUpQueueServing()

        await this.getStatus()
    },

    async verifyPin(pin, sdc = null, queue = true) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        if(queue) return await this.enqueue('verifyPin', { pin, sdc })

        const sdcType = this.getSdcType(sdc)
        const result = await this.service.verifyPin(pin, sdcType)

        if(result.error) {
            return this.formatError(result.error, sdcType)
        }

        const data = result.response?.data
        return { data: data, success: data == '0100' || data == '"0100"' }
    },

    async createInvoice(invoice, sdc = null, queue = true) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        if(queue) return await this.enqueue('createInvoice', { invoice, sdc })

        const sdcType = this.getSdcType(sdc)
        const result = await this.service.createInvoice(invoice, sdcType)

        if(result.error) {
            return this.formatError(result.error, sdcType)
        }

        const data = result.response?.data
        return { data: data, success: !!data && data.messages === 'Success' }
    },

    async getInvoice(requestId, sdc = null, queue = true) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        if(queue) return await this.enqueue('getInvoice', { requestId, sdc })

        const sdcType = this.getSdcType(sdc)
        const result = await this.service.getInvoice(requestId, sdcType)

        if(result.error) {
            return this.formatError(result.error, sdcType)
        }

        const data = result.response?.data
        return { data: data, success: !!data && data.messages === 'Success' }
    },

    async getEnvironmentParameters(sdc = null, queue = true) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        if(queue) return await this.enqueue('getEnvironmentParameters', { sdc })

        const sdcType = this.getSdcType(sdc)
        const result = await this.service.getEnvironmentParameters(sdcType)

        if(result.error) {
            return this.formatError(result.error, sdcType)
        }

        const data = result.response?.data
        return { data: data, success: true }
    },

    async getStatus(sdc = null, queue = true) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        if(queue) return await this.enqueue('getStatus', { sdc })

        const sdcType = this.getSdcType(sdc)

        const result = await this.service.getStatus(sdcType)

        if(result.error) {
            return this.formatError(result.error, sdcType)
        }

        const data = result.response?.data

        this.currentTaxRates = data.currentTaxRates;
        this.allTaxRates = data.allTaxRates;

        return { data: data, success: true }
    },

    async getAttention(sdc = null, queue = true) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        if(queue) return await this.enqueue('getAttention', { sdc })

        const sdcType = this.getSdcType(sdc)
        const result = await this.service.getAttention(sdcType)

        if(result.error) {
            return this.formatError(result.error, sdcType)
        }

        const data = result.response?.data
        return { data: data, success: true }
    },

    async getUiData(sdc = null, queue = true) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        if(queue) return await this.enqueue('getUiData', { sdc })

        const sdcType = this.getSdcType(sdc)
        const result = await this.service.getUiData(sdcType)

        if(result.error) {
            return this.formatError(result.error, sdcType)
        }

        const data = result.response?.data

        if (data && (typeof data.taxpayer === 'string')) {
            try {
                data.taxpayer = JSON.parse(data.taxpayer);
            } catch (error) {
                console.log('taxpayer was a string but could not be parsed as a valid JSON object');
            }
        }

        return { data: data, success: true }
    },

    async storeLocalAuditFiles(sdc = null, queue = true) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        if(queue) return await this.enqueue('storeLocalAuditFiles', { sdc })

        const sdcType = this.getSdcType(sdc)
        const result = await this.service.storeLocalAuditFiles(sdcType)

        if(result.error) {
            return this.formatError(result.error, sdcType)
        }

        const data = result.response?.data
        return { data: data, success: true }
    },

    async executeCommandsFromStorage(sdc = null, queue = true) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        if(queue) return await this.enqueue('executeCommandsFromStorage', { sdc })

        const sdcType = this.getSdcType(sdc)
        const result = await this.service.executeCommandsFromStorage(sdcType)

        if(result.error) {
            return this.formatError(result.error, sdcType)
        }

        const data = result.response?.data
        return { data: data, success: true }
    },

    async checkSdcStatus(sdc = null) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        const sdcType = this.getSdcType(sdc)
        const sdcStatus = await this.getStatus(sdcType)

        // If no SDC was found
        if(!sdcStatus.success) {
            const errorCode = this.device.platform === 'web' ? 'not_possible_from_web_app' : 'not_found'
            return { success: false, error: { code: errorCode, message: i18n.t(`sdc.status.${errorCode}`) } }
        }

        const statusCodes = sdcStatus.data.gsc

        // If VSDC is used
        if(!statusCodes) {
            return { success: true }
        }

        // If ESDC is used
        const statuses = statusCodes.map(statusCode => this.statuses().find(status => status.code === statusCode))
        const warnings = statuses.filter(st => !!st && st.level === 'warning')
        const errors = statuses.filter(st => !!st && st.level === 'error')

        if(errors.length) {
            return { success: false, error: { code: errors[0].label, message: errors[0].message } }
        }

        const smartCardNotPresent = warnings.find(error => error.label === 'smart_card_not_present')
        if(smartCardNotPresent) {
            return { success: false, error: { code: smartCardNotPresent.label, message: smartCardNotPresent.message } }
        }

        if(sdcStatus.data.isPinRequired) {
            return { success: false, error: { code: 'pin_required', message: i18n.t('sdc.status.pin_required') } }
        }

        return { success: true, data: sdcStatus.data }
    },

    async chooseCertificate(password, pac, queue = true) {
        if(!this.service) return { success: false, error: { code: 'not_initialized', message: i18n.t(`sdc.status.not_initialized`) } }

        if(queue) return await this.enqueue('chooseCertificate', { password, pac })

        const chooseCertificateResult = await this.service.chooseCertificate(password, pac)

        if(chooseCertificateResult.success) {
            this.isCertificateSetup = true
        }

        return chooseCertificateResult
    },

    getSdcType(sdc) {
        if(sdc !== null) return sdc

        if(this.useDevEsdc) return 'DEVSDC'

        return Store.getters.isOnline && this.isCertificateSetup ? 'VSDC' : 'ESDC'
    },

    /**
     * Calculate taxes for the invoice
     *
     * @param {Object} invoice Invoice request coming from POS
     * @returns {Array} Array of the tax items
     */
    calculateTaxes(invoice) {
        if(!this.currentTaxRates) return null

        // Choose tax rate group that needs to be applied
        let appliedTaxRateGroup = this.currentTaxRates

        if((invoice.transactionType === 'Refund' || invoice.invoiceType === 'Copy') && !!invoice.referentDocumentDT && !!invoice.referentDocumentNumber) {
            const allTaxRatesOrdered = this.allTaxRates.sort((a, b) => new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime())
            const refDate = new Date(invoice.referentDocumentDT)

            for(let i = 0; i < allTaxRatesOrdered.length; i++) {
                const validFrom = new Date(allTaxRatesOrdered[i].validFrom)

                if(refDate.getTime() >= validFrom.getTime()) {
                    appliedTaxRateGroup = allTaxRatesOrdered[i]
                }
            }
        }

        const allTaxItems = []
        invoice.items.forEach(item => {
            let totalAmount = item.totalAmount

            const itemTaxRates = item.labels.map(label => {
                const taxCategory = appliedTaxRateGroup.taxCategories.find(category => {
                    return category.taxRates.map(rate => rate.label).includes(label)
                })

                const taxRate = taxCategory.taxRates.find(rate => rate.label === label)
                return { ...taxRate, category: taxCategory }
            })

            itemTaxRates
                .filter(taxRate => taxRate.category.categoryType === 'AmountPerQuantity' || taxRate.category.categoryType == 2)
                .forEach(taxRate => {
                    const rateItem = this.calculateTaxItem(item, taxRate, itemTaxRates, totalAmount)
                    totalAmount = Money.parse(totalAmount).subtract(Money.parse(rateItem.amount)).toRoundedUnit(4)
                    allTaxItems.push(rateItem)
                })

            itemTaxRates
                .filter(taxRate => taxRate.category.categoryType !== 'AmountPerQuantity' && taxRate.category.categoryType != 2)
                .forEach(taxRate => {
                    allTaxItems.push(this.calculateTaxItem(item, taxRate, itemTaxRates, totalAmount))
                })
        })

        // Sum tax items for the same labels
        const distinctLabels = allTaxItems.map(item => item.label)
            .filter((label, index, array) => index === array.indexOf(label))

        const taxItems = distinctLabels.map(label => {
            const labelTaxItems = allTaxItems.filter(item => item.label === label)
            const labelTaxItemsAmount = labelTaxItems.reduce((acc, cur) => acc.add(Money.parse(cur.amount)), Money.parse(0))
            return { ...labelTaxItems[0], amount: labelTaxItemsAmount.toRoundedUnit(4) }
        })

        // Sum tax amounts for label's categories
        const taxCategories = []
        taxItems.forEach(taxItem => {
            const taxCategory = appliedTaxRateGroup.taxCategories.find(category => {
                return category.taxRates.map(rate => rate.label).includes(taxItem.label)
            })

            const addedTaxCategory = taxCategories.find(category => category.orderId === taxCategory.orderId)
            if(!addedTaxCategory) {
                taxCategories.push({ ...taxCategory, amount: taxItem.amount })
            } else {
                addedTaxCategory.amount = Money.parse(addedTaxCategory.amount)
                    .add(Money.parse(taxItem.amount))
                    .toRoundedUnit(4)
            }
        })

        return {
            taxItems: taxItems,
            taxCategories: taxCategories,
            taxGroupRevision: appliedTaxRateGroup.groupId,
        }
    },

    /**
     * Calculate taxes for a single invoice item
     *
     * @param {Object} item Invoice item
     * @param {Object} taxRate Tax rate object for that item from TaxCore API
     * @param {Array} itemTaxRates All tax rates applied to that item (each including tax category)
     * @param {number} totalAmount Total item amount used for tax calculation
     * @returns {Object} Tax item object
     */
    calculateTaxItem(item, taxRate, itemTaxRates, totalAmount = null) {
        totalAmount = Money.parse(totalAmount || item.totalAmount)

        const result = {
            amount: null,
            categoryType: null,
            categoryName: taxRate.category.name,
            label: taxRate.label,
            rate: taxRate.rate,
        }

        // If label is in category AmountPerQuantity
        if(taxRate.category.categoryType === 'AmountPerQuantity' || taxRate.category.categoryType == 2) {
            result.categoryType = 2
            result.amount = Money.parse(taxRate.rate)
                .multiply(item.quantity)
                .toRoundedUnit(4)

            return result
        }

        const taxRatesOnNet = itemTaxRates.filter(rate => rate.category.categoryType === 'TaxOnNet' || rate.category.categoryType == 0)
        const taxRatesOnNetSum = taxRatesOnNet.reduce((acc, cur) => acc.add(Money.parse(cur.rate)), Money.parse(0))

        const taxRatesOnTotal = itemTaxRates.filter(rate => rate.category.categoryType === 'TaxOnTotal' || rate.category.categoryType == 1)
        const taxRatesOnTotalSum = taxRatesOnTotal.reduce((acc, cur) => acc.add(Money.parse(cur.rate)), Money.parse(0))

        // If item has no labels in category TaxOnTotal
        if(!taxRatesOnTotal.length) {
            result.categoryType = 0
            result.amount = totalAmount
                .multiply(taxRate.rate)
                .divide(Money.parse(100).add(taxRatesOnNetSum).toUnit())
                .toRoundedUnit(4)

            return result
        }

        // If item has labels in category TaxOnTotal and this label is in that category
        if(taxRate.category.categoryType === 'TaxOnTotal' || taxRate.category.categoryType == 1) {
            result.categoryType = 1
            result.amount = totalAmount
                .divide(Money.parse(1).add(taxRatesOnTotalSum.divide(100)).toUnit())
                .multiply(taxRate.rate)
                .divide(100)
                .toRoundedUnit(4)

            return result
        }

        // If item has labels in category TaxOnTotal and this label is not in that category
        result.categoryType = 0
        result.amount = totalAmount
            .divide(Money.parse(1).add(taxRatesOnTotalSum.divide(100)).toUnit())
            .multiply(taxRate.rate)
            .divide(Money.parse(100).add(taxRatesOnNetSum).toUnit())
            .toRoundedUnit(4)

        return result
    },

    formatError(error, sdcType) {
        const errorStatus = this.statuses().find(status => status.code === error.code)
        if(errorStatus) {
            error = errorStatus
        }
        console.log(`${sdcType} response error: ` + (error.message || error.code))
        return { data: null, success: false, error: error }
    },

    statuses() {
        return [
            { code: '0000', level: 'info', label: 'ok', message: i18n.t('sdc.status.ok') },
            { code: '0100', level: 'info', label: 'pin_valid', message: i18n.t('sdc.status.pin_valid') },
            { code: '0210', level: 'info', label: 'internet_available', message: i18n.t('sdc.status.internet_available') },
            { code: '0220', level: 'info', label: 'internet_unavailable', message: i18n.t('sdc.status.internet_unavailable') },
            { code: '1100', level: 'warning', label: 'storage_full', message: i18n.t('sdc.status.storage_full') },
            { code: '1300', level: 'warning', label: 'smart_card_not_present', message: i18n.t('sdc.status.smart_card_not_present') },
            { code: '1400', level: 'warning', label: 'audit_requred', message: i18n.t('sdc.status.audit_requred') },
            { code: '1500', level: 'warning', label: 'pin_required', message: i18n.t('sdc.status.pin_required') },
            { code: '1999', level: 'warning', label: 'unidentified_warning', message: i18n.t('sdc.status.unidentified_warning') },
            { code: '2100', level: 'error', label: 'pin_invalid', message: i18n.t('sdc.status.pin_invalid') },
            { code: '2110', level: 'error', label: 'card_locked', message: i18n.t('sdc.status.card_locked') },
            { code: '2210', level: 'error', label: 'se_locked', message: i18n.t('sdc.status.se_locked') },
            { code: '2220', level: 'error', label: 'se_communication_failed', message: i18n.t('sdc.status.se_communication_failed') },
            { code: '2230', level: 'error', label: 'se_protocol_mismatch', message: i18n.t('sdc.status.se_protocol_mismatch') },
            { code: '2310', level: 'error', label: 'invalid_tax_labels', message: i18n.t('sdc.status.invalid_tax_labels') },
            { code: '2400', level: 'error', label: 'not_configured', message: i18n.t('sdc.status.not_configured') },
            { code: '2800', level: 'error', label: 'field_required', message: i18n.t('sdc.status.field_required') },
            { code: '2801', level: 'error', label: 'field_value_too_long', message: i18n.t('sdc.status.field_value_too_long') },
            { code: '2802', level: 'error', label: 'field_value_too_short', message: i18n.t('sdc.status.field_value_too_short') },
            { code: '2803', level: 'error', label: 'invalid_field_length', message: i18n.t('sdc.status.invalid_field_length') },
            { code: '2804', level: 'error', label: 'field_out_of_range', message: i18n.t('sdc.status.field_out_of_range') },
            { code: '2805', level: 'error', label: 'invalid_field_value', message: i18n.t('sdc.status.invalid_field_value') },
            { code: '2806', level: 'error', label: 'invalid_data_format', message: i18n.t('sdc.status.invalid_data_format') },
            { code: '2807', level: 'error', label: 'list_too_short', message: i18n.t('sdc.status.list_too_short') },
            { code: '2808', level: 'error', label: 'list_too_long', message: i18n.t('sdc.status.list_too_long') }
        ]
    },

    /**
     * Get ESDC Url
     */
    getEsdcUrl() {
        return AppStorage.local().getItem(ESDC_URL_KEY, null);
    },

    /**
     * Set ESDC Url
     */
    async setEsdcUrl(url) {
        this.esdcUrl = url;
        AppStorage.local().setItem(ESDC_URL_KEY, url, null);

        if(!this.service) return

        await this.service.init({ esdcUrl: url })
    },

    /**
     * Remove ESDC Url
     */
    removeEsdcUrl() {
        AppStorage.local().deleteItem(ESDC_URL_KEY, null);
    },

    /**
     * Add fiscal service job to the queue and return
     * the result after the job has been served
     */
    async enqueue(name, data) {
        const id = Math.random().toString(36).replace(/[^a-z]+/g, '').substring(0, 5)
        let job = { id, name, data, promise: this.defer() }
        this.queue.push(job);
        return await job.promise;
    },

    /**
     * Serve Print Jobs
     */
    setUpQueueServing(interval = 250, timeout = 60000) {
        if(this.queueSetUp) return

        let busyTime = 0
        setInterval(() => {
            if (this.curJob && (busyTime + interval) < timeout) {
                busyTime += interval
                return
            }

            this.curJob = null
            busyTime = 0

            this.serveQueue()
        }, interval);

        this.queueSetUp = true
    },

    /**
     * Serve the next available job in the queue
     */
    async serveQueue() {
        if(this.queue.length === 0) return

        const job = this.queue[0]
        this.queue.shift()

        let result = null
        this.curJob = job.id

        if(job.name === 'verifyPin') result = await this.verifyPin(job.data.pin, job.data.sdc, false)
        else if(job.name === 'createInvoice') result = await this.createInvoice(job.data.invoice, job.data.sdc, false)
        else if(job.name === 'getInvoice') result = await this.getInvoice(job.data.requestId, job.data.sdc, false)
        else if(job.name === 'getEnvironmentParameters') result = await this.getEnvironmentParameters(job.data.sdc, false)
        else if(job.name === 'getStatus') result = await this.getStatus(job.data.sdc, false)
        else if(job.name === 'getAttention') result = await this.getAttention(job.data.sdc, false)
        else if(job.name === 'getUiData') result = await this.getUiData(job.data.sdc, false)
        else if(job.name === 'storeLocalAuditFiles') result = await this.storeLocalAuditFiles(job.data.sdc, false)
        else if(job.name === 'executeCommandsFromStorage') result = await this.executeCommandsFromStorage(job.data.sdc, false)
        else if(job.name === 'chooseCertificate') result = await this.chooseCertificate(job.data.password, job.data.pac, false)
        else result = { success: false, error: { code: 'invalid_action', message: 'Action not valid' } }

        job.promise.resolve(result)

        if(this.curJob === job.id) {
            this.curJob = null
        }
    },

    /**
     * Create deferred promise that should manually be resolved
     */
    defer() {
        let res, rej;

        let promise = new Promise((resolve, reject) => {
            res = resolve;
            rej = reject;
        });

        promise.resolve = res;
        promise.reject = rej;

        return promise;
    }
}
